import { Button, Divider, IconFont, Popover, Toast } from '@byecode/ui'
import { type VariableADTvalue, VariableType } from '@lighthouse/core'
import type { EmailParticipant, VariableTree2 } from '@lighthouse/shared'
import { emailRegex, innerTypeToFieldTypeIconMap } from '@lighthouse/shared'
import { safeToString } from '@lighthouse/tools'
import { Input } from '@mantine/core'
import { find, isEmpty } from 'rambda'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import * as CM from '../styles'
import { NodeFieldVariable } from '.'
import { TagPreview } from './TagPreview'

interface SelectEmailProps {
    upstreamOptions: VariableTree2[]
    value: EmailParticipant[] | undefined
    onChange: (value: EmailParticipant[]) => void
}

const SCxAddButton = styled(Button)`
    margin: 6px 0;
`

const SCxContent = styled.div`
    padding: 0 16px;
    display: flex;
`

const SCxTargetContainer = styled.div``

const SCxList = styled.div``

const SCxItem = styled.div`
    height: 32px;
    display: flex;
    align-items: center;
`

export const SelectEmail: React.FC<SelectEmailProps> = ({ upstreamOptions, value, onChange }) => {
    const [inputValue, setInputValue] = useState('')
    const [opened, setOpened] = useState(false)
    const dropDownRef = useRef<HTMLDivElement>(null)
    const targetRef = useRef<HTMLDivElement>(null)
    const disabledWithVersion = useIsDisabledWithVersion()
    const list = useMemo(() => value || [], [value])

    const previewItemRender = useCallback(
        (item: EmailParticipant, index: number) => {
            switch (item.type) {
                case 'UPSTREAM': {
                    const node = find(node => node.value === item.upstreamVariable?.nodeId, upstreamOptions)
                    const field = find(f => f.value === item.upstreamVariable?.fieldId, node?.children || [])

                    return (
                        field && (
                            <TagPreview
                                onClick={() => onChange(list.filter((_, i) => i !== index))}
                                label={
                                    <SCxItem>
                                        <CM.Text size={12}>{node?.label}</CM.Text>
                                        {field.fieldInnerType && (
                                            <CM.Icon type={innerTypeToFieldTypeIconMap[field.fieldInnerType]} style={{ margin: '0 4px' }} />
                                        )}
                                        <CM.Text size={12}>{field?.label}</CM.Text>
                                    </SCxItem>
                                }
                            />
                        )
                    )
                }

                case 'ARG_VARIABLE': {
                    const node = find(node => node.value === item?.argVariable?.nodeId, upstreamOptions)
                    const arg = find(arg => arg.value === item.argVariable?.argId, node?.children ?? [])

                    if (!arg) {
                        return
                    }
                    return (
                        <TagPreview
                            onClick={() => onChange(list.filter((_, i) => i !== index))}
                            label={
                                <SCxItem>
                                    <CM.Text size={12}>{node?.label}</CM.Text>
                                    {node?.icon && <CM.Icon type={node?.icon} style={{ margin: '0 4px' }} />}
                                    <CM.Text size={12}>{arg.label}</CM.Text>
                                </SCxItem>
                            }
                        />
                    )
                }

                case 'VALUE': {
                    return (
                        <TagPreview
                            onClick={() => onChange(list.filter((_, i) => i !== index))}
                            icon="PropertyEmail"
                            label={safeToString(item.valueVariable?.value) ?? ''}
                        />
                    )
                }
                default: {
                    return null
                }
            }
        },
        [list, onChange, upstreamOptions]
    )

    const handleAdd = useCallback(() => {
        if (isEmpty(inputValue)) {
            setOpened(false)
            return
        }
        if (!emailRegex.test(inputValue)) {
            return Toast.warning('不符合邮箱格式')
        }
        onChange([
            ...list,
            {
                type: VariableType.VALUE,
                valueVariable: {
                    type: 'email',
                    value: inputValue
                }
            }
        ])
        setInputValue('')
        setOpened(false)
    }, [inputValue, list, onChange])

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.code === 'Enter') {
                handleAdd()
            }
        },
        [handleAdd]
    )

    const handleVariableChange = useCallback(
        (variable: VariableADTvalue) => {
            if (variable.type === VariableType.UPSTREAM || variable.type === VariableType.ARG_VARIABLE) {
                onChange([...list, variable])
                setOpened(false)
            }
        },
        [list, onChange]
    )

    return (
        <>
            <Popover position="bottom-start" disabled={disabledWithVersion} opened={opened} width={280} onClose={() => setOpened(false)}>
                <Popover.Target>
                    <SCxTargetContainer ref={targetRef}>
                        <SCxList>
                            {list.map((item, index: number) => (
                                <SCxItem key={index}>{previewItemRender(item, index)}</SCxItem>
                            ))}
                        </SCxList>
                        <SCxAddButton
                            disabled={disabledWithVersion}
                            radius={100}
                            onClick={() => setOpened(true)}
                            icon={<IconFont type="Add" size={16} fill="var(--color-gray-500)" />}
                        >
                            添加
                        </SCxAddButton>
                    </SCxTargetContainer>
                </Popover.Target>
                <Popover.Dropdown>
                    <div ref={dropDownRef}>
                        <SCxContent>
                            <Input
                                value={inputValue}
                                icon={<IconFont type="PropertyEmail" size={16} fill="var(--color-gray-500)" />}
                                onChange={e => setInputValue(e.target.value)}
                                styles={{
                                    wrapper: {
                                        flex: 1
                                    },
                                    input: {
                                        borderStyle: 'none',
                                        fontSize: 'var(--font-size-normal)',
                                        paddingLeft: '24px!important'
                                    },
                                    icon: {
                                        width: 16
                                    }
                                }}
                                placeholder="请输入邮箱按回车添加"
                                onKeyDown={handleKeyDown}
                            />
                            <CM.Icon
                                type="ArrowElbowDownLeft"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleAdd()}
                                color="var(--color-gray-400)"
                            />
                        </SCxContent>
                        <Divider color="var(--color-gray-200)" />
                        <SCxContent>
                            {/* <Group style={{ height: 36 }} spacing={8}>
                            <IconFont type="UserRole" size={16} fill="var(--color-gray-500)" />
                            <Text size={14}> 流程触发人 </Text>
                        </Group> */}
                            <NodeFieldVariable innerTypes={['TEXT']} upstreamOptions={upstreamOptions} onSelect={handleVariableChange} />
                        </SCxContent>
                    </div>
                </Popover.Dropdown>
            </Popover>
        </>
    )
}
